/**
 * The wire format between the terrain system and the tile workers.
 *
 * Everything crossing this boundary is either a plain number or a transferable — typed arrays
 * and `ImageBitmap`s — and every transferable is listed in the `transfer` array so it moves
 * rather than being structured-cloned. A 33×33 tile is about 50 KB of vertex data plus a 256 KB
 * decoded image; cloning that per tile, at the rate a fast aircraft eats tiles, is exactly the
 * kind of hidden copy that turns a 2 ms streaming budget into a 20 ms one.
 *
 * Kept in its own file so the worker and the main thread cannot drift apart: both import these
 * types, so a change to the protocol is a type error rather than a silent field mismatch.
 */

/** Build a tile mesh. */
export interface MeshTileRequest {
  type: 'mesh';
  /** Packed `(z, x, y)` — see tileId.ts. Echoed back so the reply can be matched up. */
  key: number;
  zoom: number;
  x: number;
  y: number;

  /** Where to fetch elevation; `''` skips it and produces a flat tile. */
  demUrl: string;
  /**
   * The window of {@link demUrl}'s raster that this tile covers, normalised to 0…1.
   *
   * `0, 0, 1` whenever the elevation source publishes this tile's own zoom. Deeper than that,
   * {@link demUrl} names the nearest *ancestor* the source does publish and these three numbers
   * say which part of it to read — which is what lets the quadtree follow the imagery past the
   * elevation cap instead of stopping at the shallower of the two. See `BuildTileMeshParams`.
   */
  demU0: number;
  demV0: number;
  demSpan: number;
  /**
   * Second imagery source, tried when the first returns nothing.
   *
   * NAIP covers the United States and 404s everywhere else — including over open water, which a
   * bounding box cannot express and which matters because the flagship airport is on a bay. So
   * the *service* decides coverage and this is where it falls back to. Empty when there is no
   * second source.
   */
  imageryFallbackUrl: string;
  /** Where to fetch imagery; `''` skips it and the tile renders untextured. */
  imageryUrl: string;

  /** Tile extent [deg]. */
  westDeg: number;
  southDeg: number;
  eastDeg: number;
  northDeg: number;

  /** Vertices along each edge of the tile grid, e.g. 33. */
  gridSize: number;
  /**
   * How far the skirt hangs below the tile edge [m]. Large enough to cover the height
   * difference between neighbouring LODs, small enough not to be visible from below.
   */
  skirtDepthM: number;

  /**
   * Priority for the fetch queue — distance from the camera [m]. Lower is more urgent.
   * Sent with the request rather than inferred, because only the main thread knows where the
   * camera is by the time this is picked up.
   */
  priority: number;
}

/** Cancel a queued tile that is no longer wanted. */
export interface CancelTileRequest {
  type: 'cancel';
  key: number;
}

/** Re-prioritise a tile that has not started loading. */
export interface RepriorityRequest {
  type: 'reprioritise';
  key: number;
  priority: number;
}

/** Drop everything not yet started, e.g. after a teleport to another airport. */
export interface FlushRequest {
  type: 'flush';
}

export type TileWorkerRequest =
  | MeshTileRequest
  | CancelTileRequest
  | RepriorityRequest
  | FlushRequest;

/** A finished tile. All typed arrays are transferred, not copied. */
export interface MeshTileResponse {
  type: 'mesh';
  key: number;

  /**
   * Vertex positions [m] in the tile's **own tangent frame**, anchored at the tile centre:
   * `x` east, `y` up, `z` south (the render convention).
   *
   * Tile-local rather than world-local for two reasons. It keeps the numbers small, so FP32
   * vertex buffers stay exact even for a 40 km tile; and it makes the mesh independent of the
   * floating origin, so a rebase re-places tiles with a matrix instead of re-meshing the world.
   * Because the frame is a true tangent frame at the tile centre, the Earth's curvature is
   * already baked into the geometry — the horizon dips because the ground really is curved,
   * not because a shader bent it.
   */
  positions: Float32Array;
  normals: Float32Array;
  /**
   * The height each vertex would have on the next coarser grid [m], for geomorphing.
   * Blending `position.y` toward this as a tile approaches its LOD switch makes the transition
   * continuous, which is what stops the ground visibly rippling as you fly.
   */
  morphHeights: Float32Array;
  /**
   * Texture coordinates and the triangle list are deliberately **not** here. Both are pure
   * functions of `gridSize`, which the main thread already knows because it sent it, so
   * shipping them per tile would be 140 KB of identical bytes crossing the boundary and then
   * sitting on the heap once per resident tile. The main thread builds them once with
   * {@link tileUvs} / {@link tileIndices} and shares them across every tile.
   */
  /**
   * The raw `gridSize × gridSize` height field [m MSL], row-major from the north-west corner.
   *
   * Sent as well as the geometry because the *simulation* needs ground elevation — the landing
   * gear raycasts against it every physics tick, the crash test compares against it, and ground
   * effect scales with it. Reading it back off the vertex buffer would mean undoing the tangent
   * projection on the hot path; four kilobytes a tile buys a direct lookup instead.
   */
  heightGrid: Float32Array;

  /** Decoded imagery, or null when there was none to fetch or the fetch failed. */
  imagery: ImageBitmap | null;

  /** Height range over the tile [m] — bounding volumes, and the ocean test. */
  minHeightM: number;
  maxHeightM: number;
  /** Fraction of samples at or below sea level, 0…1. Drives the water surface. */
  waterFraction: number;
  /** True when elevation came back; false means this is a flat placeholder. */
  hasElevation: boolean;

  /** How long the worker spent, split so the F3 overlay can attribute it [ms]. */
  fetchMs: number;
  decodeMs: number;
  meshMs: number;
}

/** A tile that could not be built. The terrain system retries or leaves a hole. */
export interface TileErrorResponse {
  type: 'error';
  key: number;
  message: string;
  /** True when the tile was cancelled rather than genuinely failing. */
  cancelled: boolean;
}

/** Periodic worker health, for the overlay. */
export interface TileStatsResponse {
  type: 'stats';
  queued: number;
  inFlight: number;
  completed: number;
  failed: number;
  retried: number;
  cancelled: number;
  /** True once the Cache API has been opened successfully. */
  byteCacheAvailable: boolean;
}

export type TileWorkerResponse = MeshTileResponse | TileErrorResponse | TileStatsResponse;

/**
 * Vertices in a tile mesh, skirts included.
 *
 * The skirt is one extra ring around the grid — `4 × (gridSize − 1)` quads hanging straight
 * down — which hides the sub-pixel cracks where a tile meets a neighbour at a different LOD.
 * Cheaper and far more robust than trying to stitch edges: stitching needs every neighbour's
 * LOD known at mesh time, which it is not, because neighbours stream in independently.
 */
export function tileVertexCount(gridSize: number): number {
  return gridSize * gridSize + 4 * gridSize;
}

/** Triangle indices in a tile mesh, skirts included. */
export function tileIndexCount(gridSize: number): number {
  const quads = (gridSize - 1) * (gridSize - 1);
  const skirtQuads = 4 * (gridSize - 1);
  return (quads + skirtQuads) * 6;
}
